import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { format, parseISO, isToday, isYesterday, formatDistanceToNow } from 'date-fns';
import { fi } from 'date-fns/locale';

// Tailwind class merger
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// Päivämäärän muotoilu
export function formatDate(date: string | Date, formatStr: string = 'd.M.yyyy'): string {
  const d = typeof date === 'string' ? parseISO(date) : date;
  return format(d, formatStr, { locale: fi });
}

// Suhteellinen päivämäärä
export function formatRelativeDate(date: string | Date): string {
  const d = typeof date === 'string' ? parseISO(date) : date;
  
  if (isToday(d)) return 'Tänään';
  if (isYesterday(d)) return 'Eilen';
  
  return formatDistanceToNow(d, { addSuffix: true, locale: fi });
}

// Viikonpäivä suomeksi
export function formatWeekday(date: string | Date): string {
  const d = typeof date === 'string' ? parseISO(date) : date;
  return format(d, 'EEEE', { locale: fi });
}

// Painon muotoilu
export function formatWeight(weight: number | null): string {
  if (weight === null) return '-';
  return `${weight} kg`;
}

// Keston muotoilu
export function formatDuration(minutes: number | null): string {
  if (minutes === null) return '-';
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return mins > 0 ? `${hours}h ${mins}min` : `${hours}h`;
}

// RPE-väri
export function getRpeColor(rpe: number | null): string {
  if (rpe === null) return 'text-gray-400';
  if (rpe <= 5) return 'text-green-500';
  if (rpe <= 7) return 'text-yellow-500';
  if (rpe <= 8) return 'text-orange-500';
  return 'text-red-500';
}

// Arvosana tähtinä
export function formatRating(rating: number | null): string {
  if (rating === null) return '';
  return '⭐'.repeat(rating);
}

// Treeni-tyypin nimi suomeksi
export function getWorkoutTypeName(type: string | null): string {
  const types: Record<string, string> = {
    push: 'Push',
    pull: 'Pull',
    legs: 'Jalat',
    upper: 'Yläkroppa',
    lower: 'Alakroppa',
    full_body: 'Kokovartalo',
    cardio: 'Cardio',
    other: 'Muu',
  };
  return types[type || ''] || type || 'Ei määritelty';
}

// Lihasryhmän nimi suomeksi
export function getMuscleGroupName(group: string | null): string {
  const groups: Record<string, string> = {
    chest: 'Rinta',
    back: 'Selkä',
    shoulders: 'Olkapäät',
    legs: 'Jalat',
    arms: 'Käsivarret',
    core: 'Keskivartalo',
    cardio: 'Cardio',
  };
  return groups[group || ''] || group || '-';
}

// Generoi uniikki ID
export function generateId(): string {
  return Math.random().toString(36).substring(2, 11);
}

// Tänään ISO-muodossa
export function getTodayISO(): string {
  return format(new Date(), 'yyyy-MM-dd');
}
